import { ContractOrderType, ContractServices, OrderDirection, ReduceOnly } from 'network'
import { useAssetsStore, useBaseInfoStore, useDoubleContractAssetsAccountStore, useQuoteStore } from 'store'
import { useExchangeInfoStore } from 'store/src/stores/use-exchange-info'
import { UiUtils } from '../ui-utils/ui-utils'
import { useI18n } from 'i18n-utils'

export const useClosePosition = (position: ApiResponse.DoubleContractPosition) => {
  const closePositionVisible = ref(false)
  const exchangeStore = useExchangeInfoStore()
  const baseInfoStore = useBaseInfoStore()
  const quoteStore = useQuoteStore()
  const limitPrice = ref('')
  const amount = ref(`${position.positionAmt}`)
  const sliderValue = ref(100)
  const { t } = useI18n()
  const loading = ref(false)
  const doubleContractAssetsAccount = useDoubleContractAssetsAccountStore()
  const assetStore = useAssetsStore()

  const orderType = ref(ContractOrderType.Market)

  const open = () => {
    closePositionVisible.value = true
  }

  const close = () => {
    closePositionVisible.value = false
  }

  const getSymbolInfo = () => {
    return exchangeStore.getContractSymbolInfoBySymbolId(position.symbolId)
  }

  const getSymbolIcon = () => {
    const symbolInfo = getSymbolInfo()
    if (!symbolInfo?.baseItemId) return ''
    const config = baseInfoStore.getCoinInfoByItemId(symbolInfo!.baseItemId)
    return config?.itemDetail.coinUrl
  }

  const symbolInfo = computed(() => getSymbolInfo())

  const markPrice = computed(() => {
    const quote = quoteStore.getSymbolBySymbolIdWithContractList(position.symbolId)
    return quote?.markPrice
  })

  const switchOrderType = () => {
    orderType.value = orderType.value === ContractOrderType.Market ? ContractOrderType.Limit : ContractOrderType.Market
    if (orderType.value === ContractOrderType.Market) {
      limitPrice.value = ''
    } else {
      limitPrice.value = markPrice?.value?.toString() || ''
    }
  }

  const getMarks = (value: number) => {
    return `${value}%`
  }

  const sliderMarks = computed(() => {
    // 只有当slider的值等对应的百分比时显示marks，其他都显示空
    const marks = {
      0: getMarks(0),
      25: getMarks(25),
      50: getMarks(50),
      75: getMarks(75),
      100: getMarks(100),
    }
    return marks
  })
  const onAmountChange = (value: number, positionAmt: number) => {
    if (value <= 0) {
      sliderValue.value = 0
      return
    }
    if (value >= positionAmt) {
      sliderValue.value = 100
      return
    }
    sliderValue.value = +((value / positionAmt) * 100).cutDecimal(2)
  }
  const onSliderInput = (value: number) => {
    sliderValue.value = value
    amount.value = position.positionAmt.multiplyStr(value.divideStr(100)).cutDecimal(symbolInfo.value?.quantityPrecision)
    if (amount.value.toFloatNumber().isLessThanOrEqualTo(0)) {
      sliderValue.value = 0
    }
  }

  /** 预计盈亏 */
  const estimatedProfit = computed(() => {
    const price = orderType.value === ContractOrderType.Market ? markPrice.value : limitPrice.value
    if (!price) return 0
    return price
      .subtractStr(position.holdPrice)
      .multiplyStr(amount.value)
      .multiplyStr(position.direction === OrderDirection.Buy ? 1 : -1)
      .adjustDecimal(2)
  })

  const onClosePosition = async () => {
    if (amount.value.toFloatNumber().isLessThanOrEqualTo(0)) {
      throw UiUtils.showError(t('请输入数量'))
    }
    const isMarket = orderType.value === ContractOrderType.Market
    if (!isMarket && !limitPrice.value) {
      throw UiUtils.showError(t('请输入价格'))
    }
    loading.value = true
    // TODO: 目前没有跟单功能，所以平仓暂时只兼容普通的合约平仓
    const res = await ContractServices.doubleContractOpenPosition({
      symbolId: position.symbolId.toString(),
      qty: amount.value.divideStr(position.contractValue),
      type: isMarket ? ContractOrderType.Market : ContractOrderType.Limit,
      price: isMarket ? '' : limitPrice.value,
      direction: position.direction === OrderDirection.Buy ? OrderDirection.Sell : OrderDirection.Buy,
      positionType: position.direction,
      reduceOnly: ReduceOnly.Yes,
    })
    if (res.status === 200) {
      UiUtils.showSuccess(t('平仓成功'))
      doubleContractAssetsAccount.refresh()
      assetStore.refresh()
      close()
    }
    loading.value = false
  }

  return {
    closePositionVisible,
    open,
    close,
    getSymbolIcon,
    symbolInfo,
    markPrice,
    orderType,
    switchOrderType,
    limitPrice,
    amount,
    sliderValue,
    sliderMarks,
    onSliderInput,
    onAmountChange,
    estimatedProfit,
    onClosePosition,
    loading,
  }
}
